import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../../lib/admin-api/index.js';
import { getRuntimeConfig } from '../../../lib/runtime-config.js';
import { useAuth } from '../../../context/auth-context.jsx';
import { publishAdminNotification } from '../../../shared/realtime/admin-notification-events.js';
import {
  isAdminRealtimeEvent,
  publishAdminRealtimeEvent,
} from '../../../shared/realtime/admin-realtime-events.js';

function normalizeNotification(item) {
  return {
    uid: String(item?.uid ?? ''),
    type: String(item?.type ?? ''),
    title: String(item?.title ?? ''),
    url: String(item?.url ?? ''),
    createdAt: item?.createdAt ?? null,
  };
}

export function useNotifications() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [count, setCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const esRef = useRef(null);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  // Fetch count on mount; realtime delivery keeps it fresh afterwards.
  const fetchCount = useCallback(async () => {
    if (!tokenRef.current) return;
    try {
      const res = await adminApi.getNotificationCount(tokenRef.current);
      setCount(Number(res?.count ?? 0));
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    fetchCount();
  }, [token, fetchCount]);

  // Load full list when dropdown opens
  useEffect(() => {
    if (!isOpen || !token) return;
    let isMounted = true;

    const load = async () => {
      setIsLoading(true);
      try {
        const res = await adminApi.getNotifications(token);
        if (isMounted) {
          const items = Array.isArray(res?.notifications) ? res.notifications : [];
          setNotifications(items.map(normalizeNotification));
          setCount(items.length);
        }
      } catch {
        // non-critical
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    load();
    return () => { isMounted = false; };
  }, [isOpen, token]);

  // Mercure EventSource subscription
  useEffect(() => {
    if (!token) return;

    let isMounted = true;

    const connect = async () => {
      try {
        const res = await adminApi.getMercureToken(token);
        const mercureToken = res?.token;
        const topics = Array.isArray(res?.topics) ? res.topics.filter(Boolean) : [];
        if (!mercureToken || topics.length === 0 || !isMounted) return;

        const url = new URL(getRuntimeConfig().mercurePublicUrl, window.location.origin);
        topics.forEach((topic) => {
          url.searchParams.append('topic', topic);
        });
        url.searchParams.set('authorization', mercureToken);

        const es = new EventSource(url.toString());
        esRef.current = es;

        es.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (isAdminRealtimeEvent(data)) {
              publishAdminRealtimeEvent(data);
              return;
            }

            const notification = normalizeNotification(data);
            publishAdminNotification(notification);
            setNotifications((prev) => [notification, ...prev]);
            setCount((prev) => prev + 1);
          } catch {
            // ignore malformed messages
          }
        };

        es.onerror = () => {
          es.close();
          esRef.current = null;
        };
      } catch {
        // non-critical — Mercure might not be available
      }
    };

    connect();

    return () => {
      isMounted = false;
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, [token]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  const handleClick = useCallback(async (notification) => {
    setIsOpen(false);

    // Optimistic removal
    setNotifications((prev) => prev.filter((n) => n.uid !== notification.uid));
    setCount((prev) => Math.max(0, prev - 1));

    try {
      await adminApi.deleteNotification(tokenRef.current, notification.uid);
    } catch {
      // best-effort
    }

    if (notification.url) {
      navigate(notification.url);
    }
  }, [navigate]);

  const handleClearAll = useCallback(async () => {
    setNotifications([]);
    setCount(0);
    setIsOpen(false);
    try {
      await adminApi.deleteAllNotifications(tokenRef.current);
    } catch {
      // best-effort
    }
  }, []);

  return {
    notifications,
    count,
    isOpen,
    isLoading,
    open,
    close,
    toggle,
    handleClick,
    handleClearAll,
  };
}
